import React, { useState, useRef, useEffect } from 'react';
import { formStyling } from './formElements.css';
import buttonIcon from '@images/icons/button-icon.svg';
import selectIcon from '@images/icons/select-icon.svg';
import LoaderExparel from '@components/LoaderExparel';
import { useFormContext } from 'react-hook-form';
import useSiteMetadata from '@hooks/useSiteMetadata';

// Utility: Element Block
const ElementBlock = React.forwardRef(({ label, name, children, showLabel = false, className, error }, ref) => (
  <div 
    className={`${formStyling.field} ${error ? 'error-active' : ''} ${className || ''}`}
    role="group" 
    aria-labelledby={`${name}-label`}
    ref={ref}
  >
    <label 
      id={`${name}-label`}
      htmlFor={name} 
      className={`${formStyling.label} ${className || ''} ${showLabel ? 'block' : 'hidden'}`}
    >
      {label}
    </label>
    {children}
    <div 
      className={`${formStyling.errorMessage} ${error ? 'block' : 'hidden'}`}
      id={`${name}-error`}
      role="alert"
      aria-live="polite"
    >
      {error}
    </div>
  </div>
));

// Text Input
export const TextInput = React.forwardRef(({ 
    label, 
    name, 
    value, 
    onChange, 
    placeholder, 
    additionalText,
    type = 'text', 
    required, 
    className,
    error,
    ...props
}, ref) => (
  <ElementBlock label={label} name={name} error={error}>
    <div className={formStyling.fieldWrapper}>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        placeholder={placeholder || label}
        value={value}
        onChange={onChange}
        className={`${formStyling.input} ${className || ''}`}
        ref={ref}
        aria-required={required}
        aria-invalid={!!error}
        aria-describedby={error ? `${name}-error` : undefined}
        {...props}
      />
    </div>
  </ElementBlock>
));

// Bold Placeholder Text Input
export const TextInputBold = React.forwardRef(({ 
    label, 
    name, 
    value = '', 
    onChange, 
    placeholder, 
    additionalText = '',
    type = 'text', 
    required, 
    className,
    error,
    ...props
}, ref) => {
  const [inputValue, setInputValue] = useState(value);

  const handleChange = (e) => {
    setInputValue(e.target.value);
    if (onChange) {
      onChange(e);
    }
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  return (
    <ElementBlock label={label} name={name} error={error}>
      <div className={formStyling.fieldWrapper}>
        <input
          id={name}
          name={name}
          type={type}
          required={required}
          placeholder=""
          value={inputValue}
          onChange={handleChange}
          className={`${formStyling.input} ${className || ''}`}
          ref={ref}
          aria-required={required}
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : undefined}
          {...props}
        />
        {!inputValue && (
          <div className="absolute inset-0 pointer-events-none flex items-center px-[35px] text-dark_green">
            <span className="font-bold ml-[1px]">{placeholder}</span>
            {additionalText && <span className="font-normal ml-1">{additionalText}</span>}
          </div>
        )}
      </div>
    </ElementBlock>
  );
});

// TextArea Component
export const TextArea = React.forwardRef(({ 
    label, 
    name, 
    value, 
    onChange, 
    required, 
    showLabel = true,
    className,
    rows = 4,
    error,
    ...props
}, ref) => (
    <ElementBlock label={label} name={name} showLabel={true} className="text-center" error={error}>
      <div className={formStyling.fieldWrapper}>
        <textarea
          id={name}
          name={name}
          onChange={onChange}
          required={required}
          ref={ref}
          className={`${formStyling.textbox} ${className || ''}`}
          rows={rows}
          aria-required={required}
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : undefined}
          {...props}
        />
      </div>
    </ElementBlock>
));

// Email Input
export const EmailInput = React.forwardRef((props, ref) => (
  <TextInput {...props} type="email" ref={ref} />
));

// Number Input
export const NumberInput = React.forwardRef((props, ref) => (
  <TextInput {...props} type="number" ref={ref} />
));

// Telephone Input
export const PhoneInput = React.forwardRef((props, ref) => (
  <TextInput {...props} type="tel" ref={ref} />
));

// Zip Code Input
export const ZipCodeInput = React.forwardRef((props, ref) => {
  // Handle keydown to insert hyphen automatically when typing the 6th digit

  // Format a ZIP code string to the correct format
  const formatZipCode = (value) => {
    if (!value) return '';
    
    // Remove non-digits
    const digits = value.replace(/[^\d]/g, '');
    
    // Format as ZIP+4 if more than 5 digits
    if (digits.length > 5) {
      return `${digits.slice(0, 5)}-${digits.slice(5, 9)}`;
    }
    
    return digits;
  };

  // Handle onChange to maintain correct format as user types
  const handleChange = (e) => {
    const input = e.target;
    const cursorPos = input.selectionStart;
    const oldValue = input.value;
    let newValue = formatZipCode(oldValue);
    
    // Only update if something actually changed
    if (newValue !== oldValue) {
      input.value = newValue;
      
      // Adjust cursor position if hyphen was added or removed
      let newCursorPos = cursorPos;
      
      // When adding a hyphen after 5 digits
      if (newValue.length > oldValue.length) {
        // Move cursor forward one position if we're past position 5
        if (cursorPos > 5) {
          newCursorPos = cursorPos + 1;
        }
      }
      
      // Restore cursor position
      setTimeout(() => {
        input.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
    
    // Call original onChange
    if (props.onChange) {
      // We need to create a new event with the value we want
      const newEvent = {
        ...e,
        target: {
          ...e.target,
          value: newValue
        }
      };
      props.onChange(newEvent);
    }
  };
  
  return (
    <TextInput 
      {...props} 
      type="text" 
      inputMode="numeric" 
      pattern="^\d{5}(-\d{4})?$" 
      onChange={handleChange}
      maxLength={10} // 5 digits + hyphen + 4 digits
      ref={ref} 
    />
  );
});

// Password Input
export const PasswordInput = React.forwardRef((props, ref) => (
  <TextInput {...props} type="password" ref={ref} />
));

// Select / Dropdown
export const SelectInput = React.forwardRef(({ 
  label,
  name, 
  value, 
  onChange, 
  options = [], 
  required, 
  className,
  error,
  ...props
}, ref) => (
  <ElementBlock label={label} name={name} error={error}>
    <div className={formStyling.fieldWrapper}>
      <select
        id={name}
        name={name}
        required={required}
        ref={ref}
        className={`${formStyling.select} ${className || ''}`}
        style={{ backgroundImage: `url(${selectIcon})` }}
        aria-required={required}
        aria-invalid={!!error}
        aria-describedby={error ? `${name}-error` : undefined}
        {...props}
      >
        <option value="">{label || 'Select...'}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  </ElementBlock>
));

// Checkbox
export const Checkbox = React.forwardRef(({ 
  label, 
  name, 
  onChange, 
  required, 
  className,
  error,
  ...props
}, ref) => (
  <ElementBlock label={label} name={name} error={error}>
    <div className={`${formStyling.field} flex items-start`}>
      <input
        id={name}
        name={name}
        type="checkbox"
        onChange={onChange}
        required={required}
        ref={ref}
        className={`${formStyling.checkbox} ${className || ''}`}
        aria-required={required}
        aria-invalid={!!error}
        aria-describedby={error ? `${name}-error` : undefined}
        {...props}
      />
      <label 
        htmlFor={name} 
        className="ml-2 lg:text-p text-[14px] leading-[18px] text-black/70"
      >
        {label}
      </label>
    </div>
  </ElementBlock>
));

// Submit Button
export const SubmitButton = ({ 
  children = 'Submit', 
  className, 
  addIcon = true,
  color = 'text-white',
  ...props 
}) => (
  <button 
    type="submit" 
    className={`${formStyling.submitButton} ${className || ''} ${color}`}
    aria-label={typeof children === 'string' ? children : 'Submit form'}
    {...props}
  >
    {children}
    {addIcon && (
      <img 
        src={buttonIcon} 
        alt="submit icon"
        aria-hidden="true"
        className={formStyling.buttonIcon} 
      />
    )}
  </button>
);

// Cancel Button
export const CancelButton = ({ 
  children = 'Cancel', 
  onClick, 
  className, 
  addIcon = true,
  color = 'text-white',
}) => (
  <button 
    type="button" 
    onClick={onClick} 
    className={`${formStyling.cancelButton} ${className || ''} ${color} group`}
    aria-label={typeof children === 'string' ? children : 'Cancel form'}
  >
    {children}
    {addIcon && (
      <img 
        src={buttonIcon} 
        alt="cancel icon"
        aria-hidden="true"
        className={formStyling.buttonIcon} 
      />
    )}
  </button>
);
// Form Button 
export const SimpleButton = ({ 
    children = "Sample", 
    className,
    type = 'button',
    addIcon = true,
    ariaLabel,
    ...props }) => (
  <button className={`${formStyling.simpleButton} ${className || ''}`} type={type} {...props} aria-label={ariaLabel || 'button'}>
    {children}
    {addIcon && (
      <img 
        src={buttonIcon} 
        alt={ariaLabel || 'button icon'} 
        className={formStyling.buttonIcon} 
      />
    )}
  </button>
);

// Custom Select Component
export const CustomSelect = React.forwardRef(({ 
  label, 
  name, 
  value, 
  onChange, 
  options = [], 
  required, 
  className,
  dropdownHeight = '',
  placeholder = 'Select...',
  additionalText = ' (Choose One)',
  error,
  ...props
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(
    options.find(opt => opt.value === value) || null
  );
  const selectRef = useRef(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const listboxRef = useRef(null);

  // Debug
  useEffect((options, value, selectedOption) => {
    if (value) {
      const option = options.find(opt => opt.value === value);
      if (option && (!selectedOption || selectedOption.value !== value)) {
        setSelectedOption(option);
      }
    }
  }, [value, options, selectedOption]);

  // Expose method to update value custom select
  React.useImperativeHandle(ref, () => ({
    setValue: (newValue) => {
      const option = options.find(opt => opt.value === newValue);
      if (option) {
        setSelectedOption(option);
        
        // Also update the hidden native select for form submission
        const select = document.querySelector(`select[name="${name}"]`);
        if (select) {
          select.value = newValue;
          // Dispatch change event
          const event = new Event('change', { bubbles: true });
          select.dispatchEvent(event);
        }
      }
    }
  }), [name, options]);

  // Update selected option when value changes from parent
  useEffect(() => {
    if (value) {
      const option = options.find(opt => opt.value === value);
      if (option && (!selectedOption || selectedOption.value !== value)) {
        setSelectedOption(option);
      }
    }
  }, [value, options, selectedOption]);

  const handleSelect = (option) => {
    setSelectedOption(option);
    setIsOpen(false);
    setFocusedIndex(-1);
    
    // Update the native select
    const select = document.querySelector(`select[name="${name}"]`);
    if (select) {
      select.value = option.value;
    }
    
    // Call onChange with the proper event object that React Hook Form expects
    onChange({
      target: {
        name,
        value: option.value
      },
      type: 'change',
      persist: () => {}
    });
    
    selectRef.current?.querySelector('[role="combobox"]')?.focus();
  };

  // Listen for native select changes
  useEffect(() => {
    const select = document.querySelector(`select[name="${name}"]`);
    if (select) {
      const handleChange = (e) => {
        const option = options.find(opt => opt.value === e.target.value);
        if (option && option.value !== selectedOption?.value) {
          setSelectedOption(option);
        }
      };
      select.addEventListener('change', handleChange);
      return () => select.removeEventListener('change', handleChange);
    }
  }, [name, options, selectedOption]);

  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && listboxRef.current) {
      const options = listboxRef.current.querySelectorAll('[role="option"]');
      if (options[focusedIndex]) {
        options[focusedIndex].scrollIntoView({ block: 'nearest' });
        options[focusedIndex].focus();
      }
    }
  }, [focusedIndex, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyboardNavigation = (e) => {
      if (!isOpen) return;
      
      switch(e.key) {
        case 'Home':
          e.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setFocusedIndex(options.length - 1);
          break;
        case 'PageUp':
          e.preventDefault();
          setFocusedIndex(prev => Math.max(0, prev - 5));
          break;
        case 'PageDown':
          e.preventDefault();
          setFocusedIndex(prev => Math.min(options.length - 1, prev + 5));
          break;
        default:
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyboardNavigation);
      return () => document.removeEventListener('keydown', handleKeyboardNavigation);
    }
  }, [isOpen, options.length]);

  const handleOptionKeyDown = (e, option, index) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleSelect(option);
        break;
      case 'Tab':
        e.preventDefault();
        if (e.shiftKey) {
          setFocusedIndex(prev => 
            prev > 0 ? prev - 1 : options.length - 1
          );
        } else {
          setFocusedIndex(prev => 
            prev < options.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev < options.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev > 0 ? prev - 1 : 0
        );
        break;
      case 'Escape':
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <ElementBlock label={label} name={name} error={error}>
      <div 
        ref={selectRef}
        className={`relative ${className || ''}`}
      >
        {/* Select Trigger */}
        <div className={`${formStyling.fieldWrapper} ${isOpen ? 'dropdown-open' : ''} ${error ? 'error-active' : ''} ${formStyling.customSelect}`}>
        <div
          className={`${formStyling.field} ${className || ''} ${formStyling.selectTrigger}`}
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={isOpen ? handleOptionKeyDown : null}
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={`${name}-options`}
          aria-haspopup="listbox"
					  tabIndex={0}
					  id={props.id}
        >
          <span className="text-[14px] lg:text-[16px] font-bold">
            {selectedOption ? (
              <>
                {selectedOption.label}
              </>
            ) : (
              <>
                {placeholder}
                <span className="font-light">{additionalText}</span>
              </>
            )}
          </span>
          <img
            src={selectIcon}
            alt={'select icon'}
            className={formStyling.selectFieldIcon + ' ' + (isOpen ? 'rotate-180' : '')}
          />
        </div>
        </div>

        {/* Dropdown Options */}
        <div className={`${formStyling.customSelectDropdownParent} ${className || ''} ${isOpen ? 'visible max-h-auto' : 'invisible max-h-0'} ${dropdownHeight ? dropdownHeight : 'h-[250px]'}`}>
          <div 
            ref={listboxRef}
            className={`${formStyling.customSelectDropdown} ${className || ''}`}
            id={`${name}-options`}
            role="listbox"
          >
            {options.map((option, index) => (
              <div
                key={option.value}
                className={`${formStyling.customSelectDropdownOption} ${
                  selectedOption?.value === option.value 
                    ? 'selected'
                    : ''
                } ${
                  index === focusedIndex ? 'focused' : ''
                }`}
                onClick={() => handleSelect(option)}
                onKeyDown={(e) => handleOptionKeyDown(e, option, index)}
                role="option"
                tabIndex={index === focusedIndex ? 0 : -1}
                aria-selected={selectedOption?.value === option.value}
              >
                {option.label}
              </div>
            ))}
          </div>
        </div>

        {/* Hidden native select for form submission */}
        <select
          name={name}
          value={selectedOption?.value || ''}
          onChange={() => {}}
          required={required}
          className="sr-only"
          aria-hidden="true"
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </ElementBlock>
  );
});

// Facility Search Component  
export const FacilitySearch = React.forwardRef(({
  type = 'text',
  label,
  name,
  value,
  required,
  className,
  placeholder,
  error,
  onChange,
  sectionId,
  ...props
}, ref) => {
  const [suggestions, setSuggestions] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const isClearingRef = useRef(false);
  const wrapperRef = useRef(null);
  const timeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const { apiUrl } = useSiteMetadata();

  // Update local input value when form value changes
  useEffect(() => {
    if (isClearingRef.current) {
      isClearingRef.current = false;
      return;
    }
    setInputValue(value || '');
    // Clear selected facility and suggestions when value is cleared
    if (!value) {
      setSelectedFacility(null);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [value]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    // Update form value
    if (onChange) {
      onChange(e);
    }
    setLoading(true);
    clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 1000);
  };

  const fetchSuggestions = async (input) => {
    if (!input) return;

    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      // TODO: sanitize input?
      const response = await fetch(`${apiUrl}/facility?name=${input}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const data = result.data?.facilities;

      if (!Array.isArray(data) || data.length === 0) {
        setSuggestions([{ error: "No facilities found. Please try a different search term." }]);
      } else {
        setSuggestions(data);
      }
      setShowSuggestions(true);
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error("API error", error);
      setSuggestions([{ error: "No facilities found. Please try a different search term." }]);
      setShowSuggestions(true);
    } finally {
      setLoading(false);
    }
  };

  const selectFacility = (facility) => {    
    const facilityData = {
      id: facility.hco_entity_id,
      name: facility.name,
      address: facility.address,
      city: facility.city,
      state: facility.state,
      zip: facility.zip,
      phone: facility.phone,
      latitude: facility.latitude,
      longitude: facility.longitude,
      sectionId: sectionId
    };
    setSelectedFacility(facilityData);
    setInputValue(facility.name);
    setShowSuggestions(false);
    
    // Update form value for the facility search field
    if (onChange) {
      onChange({
        target: {
          name,
          value: facility.name
        }
      });
    }
    
    // Trigger facility selected event with section-specific event name
    const eventName = sectionId ? `facility:selected:${sectionId}` : 'facility:selected';
    const facilitySelectedEvent = new CustomEvent(eventName, {
      detail: facilityData
    });
    document.dispatchEvent(facilitySelectedEvent);
  };

  // Handle form reset
  useEffect(() => {
    const handleReset = () => {
      isClearingRef.current = true;
      setInputValue('');
      setSelectedFacility(null);
      setSuggestions([]);
      setShowSuggestions(false);
    };

    // Listen for form reset event
    document.addEventListener('form:reset', handleReset);
    return () => document.removeEventListener('form:reset', handleReset);
  }, []);

  return (
    <ElementBlock label={label} name={name} error={error} className={`${formStyling.facilitySearch} hospital-info`} ref={wrapperRef}>
      <div className={`${formStyling.fieldWrapper} ${error ? 'error-active' : ''} ${formStyling.facilitySearchWrapper}`}>
        <input
          id={name}
          name={name}
          type={type}
          required={required}
          placeholder={placeholder || label}
          value={inputValue}
          onChange={handleInputChange}
          className={`${formStyling.input} ${className || ''} z-20`}
          ref={ref}
          aria-label={label}
          aria-required={required}
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : undefined}
          aria-expanded={showSuggestions}
          aria-controls={`${name}-suggestions`}
          aria-autocomplete="list"
          role="combobox"
          {...props}
        />
        {loading && (
          <div className={formStyling.facilitySearchLoading}>
            <LoaderExparel className="loader" size="30px" />
          </div>
        )}
      </div>
      {showSuggestions && (
        <div 
          className={`${formStyling.facilitySearchDropdownParent}`}
          id={`${name}-suggestions`}
          role="listbox"
          aria-label={`${label} suggestions`}
        >
          <ul 
            className={`${formStyling.facilitySearchDropdown}`}
            role="presentation"
          >
            {Array.isArray(suggestions) && suggestions.length > 0 ? (
              suggestions.map((sug, idx) =>
                sug.error ? (
                  <li 
                    key={idx} 
                    className={`${formStyling.facilitySearchDropdownItem}`}
                    role="alert"
                  >
                    {sug.error}
                  </li>
                ) : (
                  <li
                    key={idx}
                    className={`${formStyling.facilitySearchDropdownItem}`}
                    onClick={() => selectFacility(sug)}
                    role="option"
                    aria-selected={selectedFacility?.id === sug.hco_entity_id}
                    tabIndex={0}
                  >
                    <span className={`${formStyling.facilitySearchDropdownItemBold}`}>
                      {sug.name}
                    </span>
                    <span className={`${formStyling.facilitySearchDropdownSmallText}`}>
                      {sug.state},{" "}
                      {sug.city}{" "}
                      {sug.zip}
                    </span>
                  </li>
                )
              )
            ) : (
							  <li 
								  key="no-results"
                className={`${formStyling.facilitySearchDropdownItem}`}
                role="alert"
              >
                No results found. Please try a different search term.
              </li>
            )}
          </ul>
        </div>
      )}
    </ElementBlock>
  );
});

// MultiSelect Component
export const MultiSelect = React.forwardRef(({
  label,
  name,
  options = [],
  required,
  value,
  onChange,
  placeholder = 'Select options...',
  additionalText = '',
  className,
  error,
  ...props
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const selectRef = useRef(null);
  const { setValue } = useFormContext();
  
  // Handle initial values and updates from form
  useEffect(() => {
    if (value) {
      const initialSelected = Array.isArray(value) 
        ? value 
        : typeof value === 'string' ? [value] : [];
        
      setSelectedOptions(initialSelected);
    }
  }, [value]);
  
  // Handle outside clicks to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Toggle selection for an option
  const handleSelect = (optionValue) => {
    let updatedOptions;
    
    if (selectedOptions.includes(optionValue)) {
      updatedOptions = selectedOptions.filter(val => val !== optionValue);
    } else {
      updatedOptions = [...selectedOptions, optionValue];
    }
    
    setSelectedOptions(updatedOptions);
    
    // Use setValue directly from React Hook Form
    setValue(name, updatedOptions, { 
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    });
  };
  
  // Get label for a value
  const getOptionLabel = (optionValue) => {
    const option = options.find(opt => opt.value === optionValue);
    return option ? option.label : optionValue;
  };
  
  return (
    <ElementBlock label={label} name={name} error={error}>
      <div 
        ref={selectRef}
        className={`relative  ${className || ''}`}
      >
        {/* Select Trigger */}
        <div 
          className={`${formStyling.fieldWrapper} ${formStyling.customSelect} ${isOpen ? 'dropdown-open' : ''} ${error ? 'error-active' : ''} cursor-pointer`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className={`${formStyling.field} ${className || ''} ${formStyling.selectTrigger} gap-2`}>
            {selectedOptions.length === 0 ? (
              <span className="text-dark_green block lg:text-[16px] text-[14px] font-bold leading-[18px] flex gap-1 lg:flex-row flex-col">{placeholder ? placeholder : label} <span className="font-normal"> {additionalText ? `${additionalText}` : ''}</span></span>
            ) : (
              <span className="sr-only">{label} - {selectedOptions.length} selected</span>
            )}
            {selectedOptions.length > 0 && (
              <span className="truncate">
                {/* {selectedOptions.length} {label} options selected */}
                {selectedOptions
                  .map(optionValue => ({
                    value: optionValue,
                    label: getOptionLabel(optionValue)
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label))
                  .map((option, index) => (
                    <React.Fragment key={option.value}>
                      {option.label}
                      {index < selectedOptions.length - 1 ? ', ' : ''}
                    </React.Fragment>
                  ))}
              </span>
            )}
            <img
              src={selectIcon}
              alt="toggle dropdown"
              className={`${formStyling.selectFieldIcon} ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>
        </div>
        
        {/* Dropdown */}
        {isOpen && (
          <div className={`${formStyling.customSelectDropdownParent} ${className || ''}`}>
            <div 
              className={`${formStyling.customSelectDropdown} ${className || ''}`}
              id={`${name}-options`}
              role="listbox"
              aria-multiselectable="true"
            >
              {options.map((option) => (
                <div
                  key={option.value}
                  className={`${formStyling.multiSelectDropdownOption} ${formStyling.customSelectDropdownOption} flex items-center
                    cursor-pointer ${selectedOptions.includes(option.value) 
                    ? `${formStyling.multiSelectDropdownOptionSelected}` 
                    : ''}`}
                  onClick={() => handleSelect(option.value)}
                  role="option"
                  aria-selected={selectedOptions.includes(option.value)}
                >
                  <input
                    type="checkbox"
                    className="mr-2 h-4 w-4"
                    checked={selectedOptions.includes(option.value)}
                    onChange={() => handleSelect(option.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {option.label}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Hidden input for form handling */}
        <input
          type="hidden"
          name={name}
          ref={ref}
          value={selectedOptions}
          {...props}
        />
      </div>
    </ElementBlock>
  );
});

// Procedure Select Component
export const ProcedureSelect = React.forwardRef(({ 
  label, 
  name, 
  required = false, 
  error, 
  additionalText,
  placeholder = 'Select procedures...',
  options = [],
  disabled = false,
  ...props
}, ref) => {
  const { register, setValue } = useFormContext();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const selectRef = useRef(null);

  // Register the field with React Hook Form
  const { ref: formRef, ...registerProps } = register(name);

  // Handle outside clicks to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Toggle selection for an option
  const handleSelect = (optionValue) => {
    if (disabled) return;
    
    let updatedOptions;
    
    if (selectedOptions.includes(optionValue)) {
      updatedOptions = selectedOptions.filter(val => val !== optionValue);
    } else {
      updatedOptions = [...selectedOptions, optionValue];
    }
    
    setSelectedOptions(updatedOptions);
    // Update the form value
    setValue(name, updatedOptions);
  };

  // Get label for a value
  const getOptionLabel = (optionValue) => {
    for (const group of options) {
      const option = group.options.find(opt => opt.value === optionValue);
      if (option) return option.label;
    }
    return optionValue;
  };

  return (
    <ElementBlock label={label} name={name} error={error}>
      <div 
        ref={selectRef}
        className="relative"
      >
        {/* Select Trigger */}
        <div 
          className={`${formStyling.fieldWrapper} ${formStyling.customSelect} ${isOpen ? 'dropdown-open' : ''} ${error ? 'error-active' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div className={`${formStyling.field} ${formStyling.selectTrigger} gap-2`}>
            {selectedOptions.length === 0 ? (
              <span className="text-dark_green block lg:text-[16px] text-[14px] font-bold leading-[18px]">
                {disabled ? 'Procedures not available for Other specialty' : placeholder} <span className="font-normal inline-flex"> {additionalText}</span>
              </span>
            ) : (
              <span className="truncate">
                {selectedOptions
                  .map(optionValue => ({
                    value: optionValue,
                    label: getOptionLabel(optionValue)
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label))
                  .map((option, index) => (
                    <React.Fragment key={option.value}>
                      {option.label}
                      {index < selectedOptions.length - 1 ? ', ' : ''}
                    </React.Fragment>
                  ))}
              </span>
            )}
            <img
              src={selectIcon}
              alt="toggle dropdown"
              className={`${formStyling.selectFieldIcon} ${isOpen ? 'rotate-180' : ''} ${disabled ? 'opacity-50' : ''}`}
            />
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && !disabled && (
          <div className={`${formStyling.customSelectDropdownParent}`}>
            <div 
              className={`${formStyling.customSelectDropdown} max-h-[400px] overflow-y-auto`}
              id={`${name}-options`}
              role="listbox"
              aria-multiselectable="true"
            >
              {options.map((group) => (
                <div key={group.value} className="procedure-group mb-4">
                  <div className="procedure-title font-bold text-dark_green px-0 py-2 bg-gray-50">
                    {group.label}
                  </div>
                  <div className="procedure-options pl-4">
                    {group.options.map((option) => (
                      <div
                        key={option.value}
                        className={`${formStyling.multiSelectDropdownOption} ${formStyling.customSelectDropdownOption} flex items-center
                          cursor-pointer ${selectedOptions.includes(option.value) 
                          ? `${formStyling.procedureSelectOptionSelected}` 
                          : ''}`}
                        onClick={() => handleSelect(option.value)}
                        role="option"
                        aria-selected={selectedOptions.includes(option.value)}
                      >
                        <input
                          type="checkbox"
                          className="mr-2 h-4 w-4"
                          checked={selectedOptions.includes(option.value)}
                          onChange={() => handleSelect(option.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        {option.label}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Hidden input for form handling */}
        <input
          type="hidden"
          name={name}
          ref={(e) => {
            formRef(e);
            if (ref) ref.current = e;
          }}
          value={selectedOptions}
          disabled={disabled}
          {...registerProps}
        />
      </div>
    </ElementBlock>
  );
});




